import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { TranslationService } from '@scripts/woo_services.module/TranslationService';
import { ModalContent } from '@src/shared/modal/confirm-modal/confirm-modal.type';
import { ModalService } from '@src/shared/modal/modal.service';
import { CampaignSelectionModalComponent } from '@src/views/dashboard/components/modal/campaign-selection-modal/campaign-selection-modal.component';
import { GoogleTagManagerService } from '../../../woo_services.module/GoogleTagManagerService';
import { RoutingService } from '../../../woo_services.module/RoutingService';
import { CompactCampaign, CompactCustomer } from '../../../woo_services.module/shared-types';

@Component({
  selector: 'create-campaign-from-template-button',
  template: `<button
    id="duplicate-campaign-button"
    (click)="onClick()"
    class="status-badge inverse"
    [tabindex]="tabindex"
    data-toggle="tooltip"
    data-placement="bottom"
    (mouseenter)="hover.emit($event)"
  >
    <span class="icon-thumbs-single-row"></span>
    <div class="hover-text hover--below">Duplicera kampanj</div>
  </button>`,
  standalone: false,
})
export class CreateCampaignFromTemplateButton {
  translationService = inject(TranslationService);
  @Input() customersForUser: CompactCustomer[] = [];
  @Input() tabindex: number;
  @Input() disabled = false;
  @Output() hover = new EventEmitter();

  constructor(
    private googleTagManagerService: GoogleTagManagerService,
    private routingService: RoutingService,
    private modalService: ModalService,
  ) {}

  onClick = (): void => {
    if (this.disabled) {
      return;
    }

    const modal = this.modalService.openCustom(CampaignSelectionModalComponent, {
      customers: this.customersForUser,
    });

    modal.componentInstance.submit.subscribe((campaign) => {
      if (campaign) {
        const content = this.getDialogContent(campaign.name);
        this.modalService
          .openConfirm(content)
          .then(async (resp) => {
            if (resp.confirm) {
              await this.duplicateCampaign(campaign).catch((err) => {
                this.modalService.openError(
                  'Ajdå, något gick snett. Kunde inte duplicera kampanj.',
                  this.translationService.convertError(err.error.error),
                );
              });
            } else {
              modal.close();
            }
          })
          .finally(() => modal.close());
      } else {
        this.createNewCampaign();
      }
      modal.close();
    });
  };

  private async createNewCampaign() {
    await this.routingService.navigate(['/campaigns', 'new']);
  }

  private duplicateCampaign = async (campaign: CompactCampaign) => {
    this.googleTagManagerService.duplicateCampaign('specific');
    await this.routingService.navigate(['/campaigns', 'new'], { state: { id: campaign.id, templating: true } });
  };

  private getDialogContent = (campaignName: string): ModalContent => ({
    title: 'Duplicera kampanj',
    texts: [
      'Du är nu på väg att duplicera en kampanj. Det innebär att allt kopieras från den tidigare kampanjen till den nya, förutom namn och kampanjperiod. Eventuella perioder för filmerna behöver också sättas om.',
      `Vill du fortsätta duplicera kampanjen <strong>${campaignName}</strong>?`,
    ],
    confirmLabel: 'Duplicera kampanj',
    cancelLabel: 'Avbryt',
  });
}
